import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { GuideSummary } from '@/types'

import Link from 'next/link'
import { ChevronRight, Compass } from 'lucide-react'

import { Seo } from '@/components/generals/seo'
import { JsonLd } from '@/components/generals/json-ld'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { GuideCard } from '@/components/blog/guide-card'
import { Newsletter } from '@/components/blog/newsletter'

import { buildGraph, breadcrumbNode, guidesIndexNode } from '@/lib/structured-data'
import { getGuides, getGuideChapterCounts } from '@/lib/guides'

interface GuidesIndexProps {
    guides: GuideSummary[]
    counts: Record<string, number>
}

const GuidesIndexPage: FC<GuidesIndexProps> = ({ guides, counts }): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Complete guides'
                description='In-depth guides to mental health, physical health, and nutrition. Each one explains a topic end to end and links to every article we have written on it.'
                canonical='/guides'
            />
            <JsonLd
                data={buildGraph([
                    breadcrumbNode([
                        { name: 'Home', path: '/' },
                        { name: 'Guides', path: '/guides' }
                    ]),
                    guidesIndexNode(guides)
                ])}
            />

            <section id='guides-hero' className='border-b border-border bg-secondary/40'>
                <Container className='py-12 sm:py-16'>
                    <nav
                        aria-label='Breadcrumb'
                        className='flex items-center gap-1.5 text-sm text-muted-foreground'
                    >
                        <Link href='/' className='transition-colors hover:text-foreground'>
                            Home
                        </Link>
                        <ChevronRight className='size-3.5' aria-hidden='true' />
                        <span className='font-medium text-foreground'>Guides</span>
                    </nav>

                    <div className='mt-7 flex items-center gap-3'>
                        <div className='flex size-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground'>
                            <Compass className='size-6' aria-hidden='true' />
                        </div>
                        <p className='text-base font-semibold text-primary'>Start here</p>
                    </div>

                    <h1 className='mt-5 font-heading text-4xl font-bold tracking-tight sm:text-5xl'>
                        Complete guides
                    </h1>

                    <p className='mt-4 max-w-2xl text-lg leading-relaxed text-muted-foreground'>
                        Each guide covers one topic from the ground up, then points you to every article
                        underneath it in a sensible reading order. Start with the guide if the subject is
                        new to you, or jump straight to the chapter you need.
                    </p>
                </Container>
            </section>

            <section className='py-12 sm:py-16'>
                <Container>
                    {guides.length === 0 ? (
                        <div className='rounded-2xl border border-dashed border-border bg-card py-16 text-center'>
                            <p className='font-heading text-lg font-semibold'>Guides are on the way</p>
                            <p className='mx-auto mt-2 max-w-sm text-sm text-muted-foreground'>
                                We are writing the first set now. In the meantime, browse the library by topic.
                            </p>
                            <Link
                                href='/'
                                className='mt-6 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90'
                            >
                                Back to home
                            </Link>
                        </div>
                    ) : (
                        <div className='grid gap-8 lg:grid-cols-3'>
                            {guides.map(guide => (
                                <GuideCard key={guide.slug} guide={guide} chapterCount={counts[guide.id]} />
                            ))}
                        </div>
                    )}
                </Container>
            </section>

            <section className='pb-16'>
                <Container>
                    <Newsletter />
                </Container>
            </section>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<GuidesIndexProps> = async () => {
    const [guides, counts] = await Promise.all([getGuides(), getGuideChapterCounts()])

    return { props: { guides, counts } }
}

export default GuidesIndexPage
